/**
 * @fileoverview Modal de confirmação proporcional pra delete de usuário.
 *
 * Fluxo:
 *   1. Ao abrir: faz GET /users/:id/delete-impact pra carregar a "preview"
 *      (tickets por status, contatos em carteira, candidatos a transferência).
 *   2. Renderiza um card por company com action selector (transferir vs
 *      encerrar) e checkboxes (incluir carteira / incluir closed).
 *   3. Ao confirmar: faz DELETE /users/:id com body { transfers: [...] }
 *      e mostra checklist de fases enquanto aguarda o response.
 *
 * Modo close NÃO envia mensagem ao cliente (massa de N disparos seria
 * suficiente pra banir o número Meta/WAHA).
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  Typography,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  MenuItem,
  TextField,
  makeStyles
} from "@material-ui/core";
import {
  AlertTriangle,
  ArrowRightLeft,
  X,
  Briefcase,
  Users,
  MessageSquare
} from "lucide-react";
import api from "../../services/api";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { createModalDesign } from "../../styles/modalDesign";

const useStyles = makeStyles((theme) => ({
  ...createModalDesign(theme),
  wideDialog: {
    "& .MuiDialog-paper": {
      width: "min(820px, calc(100vw - 32px))"
    }
  },
  loadingBlock: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 12,
    padding: theme.spacing(4)
  },
  summary: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    padding: theme.spacing(1.5, 2),
    borderRadius: 12,
    background: theme.palette.type === "dark"
      ? "rgba(239, 68, 68, 0.10)"
      : "rgba(254, 226, 226, 0.6)",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(239, 68, 68, 0.30)"
      : "1px solid rgba(252, 165, 165, 0.6)"
  },
  summaryHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: theme.palette.type === "dark" ? "#fca5a5" : "#b91c1c",
    fontWeight: 600,
    fontSize: 14
  },
  summaryText: {
    fontSize: 13,
    color: theme.palette.type === "dark" ? "rgba(248,250,252,0.78)" : "#7f1d1d",
    lineHeight: 1.5
  },
  companyCard: {
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.08)"
      : "1px solid rgba(15,23,42,0.10)",
    borderRadius: 14,
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: 12,
    background: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.02)"
      : "#fcfcfd"
  },
  companyHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8
  },
  companyName: {
    fontSize: 15,
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "#f8fafc" : "#0f172a"
  },
  countsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8
  },
  countPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    background: theme.palette.type === "dark"
      ? "rgba(91,124,255,0.10)"
      : "rgba(91,124,255,0.08)",
    color: theme.palette.type === "dark" ? "#cbd5e1" : "#334155"
  },
  countPillEmpty: {
    opacity: 0.5
  },
  modeRow: {
    display: "flex",
    gap: 6
  },
  modeOption: {
    flex: 1,
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.08)"
      : "1px solid rgba(15,23,42,0.10)",
    borderRadius: 10,
    padding: theme.spacing(1, 1.25),
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    color: theme.palette.type === "dark" ? "#cbd5e1" : "#334155",
    transition: "all 150ms ease"
  },
  modeOptionActive: {
    borderColor: theme.palette.type === "dark" ? "#5B7CFF" : "#3D5CFD",
    background: theme.palette.type === "dark"
      ? "rgba(91,124,255,0.14)"
      : "rgba(61,92,253,0.08)",
    color: theme.palette.type === "dark" ? "#f8fafc" : "#0f172a"
  },
  options: {
    display: "flex",
    flexDirection: "column",
    gap: 4
  },
  optionCheck: {
    fontSize: 13,
    color: theme.palette.type === "dark" ? "rgba(248,250,252,0.8)" : "#334155",
    margin: 0
  },
  targetSelect: {
    width: "100%"
  },
  footerInfo: {
    fontSize: 12,
    color: theme.palette.type === "dark" ? "rgba(248,250,252,0.55)" : "#6b7280",
    padding: theme.spacing(1.25, 0, 0)
  },
  dangerBtn: {
    background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
    color: "#ffffff",
    border: "none",
    height: 36,
    minWidth: 160,
    padding: "0 18px",
    borderRadius: 10,
    fontSize: "0.875rem",
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "inherit",
    "&:hover:not(:disabled)": {
      background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)"
    },
    "&:disabled": { opacity: 0.45, cursor: "not-allowed" }
  }
}));

const ModeButton = ({ active, onClick, label, classes }) => (
  <div
    role="button"
    tabIndex={0}
    className={`${classes.modeOption} ${active ? classes.modeOptionActive : ""}`}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") onClick();
    }}
  >
    {label === "Transferir" ? <ArrowRightLeft size={15} /> : <X size={15} />}
    {label}
  </div>
);

const UserDeleteImpactModal = ({ open, userId, userName, onClose, onDeleted }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [impact, setImpact] = useState(null);
  const [plan, setPlan] = useState({}); // { [companyId]: { mode, targetUserId, includeWallet, includeClosed } }
  const [submitting, setSubmitting] = useState(false);

  const fetchImpact = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setImpact(null);
    setPlan({});
    try {
      const { data } = await api.get(`/users/${userId}/delete-impact`);
      setImpact(data);
      const initial = {};
      (data.companies || []).forEach(c => {
        initial[c.companyId] = {
          mode: "transfer",
          targetUserId: c.availableTransferUsers?.[0]?.id || null,
          includeWallet: true,
          includeClosed: false
        };
      });
      setPlan(initial);
    } catch (err) {
      toastError(err);
      onClose();
    } finally {
      setLoading(false);
    }
  }, [userId, onClose]);

  useEffect(() => {
    if (open) fetchImpact();
  }, [open, fetchImpact]);

  const updateCompanyPlan = (companyId, patch) => {
    setPlan(prev => ({
      ...prev,
      [companyId]: { ...prev[companyId], ...patch }
    }));
  };

  const validation = useMemo(() => {
    if (!impact) return { valid: false, reason: "loading" };
    for (const c of impact.companies) {
      const p = plan[c.companyId];
      if (!p) return { valid: false, reason: `Plano faltando pra ${c.companyName}` };
      const hasWork =
        c.tickets.open > 0 || c.tickets.pending > 0 || c.contactsInWallet > 0;
      if (p.mode === "transfer" && hasWork) {
        if (!p.targetUserId) {
          return {
            valid: false,
            reason: `Selecione um atendente destino pra ${c.companyName}`
          };
        }
      }
    }
    return { valid: true };
  }, [impact, plan]);

  const handleConfirm = async () => {
    if (!validation.valid || submitting) return;
    setSubmitting(true);
    try {
      const transfers = impact.companies.map(c => {
        const p = plan[c.companyId];
        if (p.mode === "transfer") {
          return {
            companyId: c.companyId,
            mode: "transfer",
            targetUserId: p.targetUserId,
            includeWallet: !!p.includeWallet,
            includeClosed: !!p.includeClosed
          };
        }
        return { companyId: c.companyId, mode: "close" };
      });

      await api.delete(`/users/${userId}`, { data: { transfers } });

      toast.success("Usuário excluído com sucesso");
      if (onDeleted) onDeleted();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      disableEscapeKeyDown={submitting}
      className={`${classes.mdDialog} ${classes.wideDialog}`}
      maxWidth={false}
    >
      <div className={classes.mdHeader}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <AlertTriangle size={20} color="#ef4444" strokeWidth={2} />
          <Typography className={classes.mdTitle}>Excluir usuário</Typography>
        </div>
      </div>

      <div className={classes.mdContent}>
        {loading || submitting ? (
          <div className={classes.loadingBlock}>
            <CircularProgress size={28} />
            <Typography style={{ fontSize: 13, opacity: 0.7 }}>
              {submitting ? "Excluindo usuário…" : "Calculando impacto da exclusão…"}
            </Typography>
          </div>
        ) : impact ? (
          <>
            <div className={classes.summary}>
              <div className={classes.summaryHeader}>
                <AlertTriangle size={16} />
                Você está prestes a excluir{" "}
                <strong>&nbsp;{impact.user.name}&nbsp;</strong>
                {impact.scope === "global"
                  ? `— afeta ${impact.companies.length} empresa(s)`
                  : "— afeta apenas sua empresa"}
              </div>
              <Typography className={classes.summaryText}>
                {impact.scope === "global"
                  ? "Como super-admin, o usuário será removido globalmente. Para cada empresa em que ele atua, defina o que fazer com tickets e carteira."
                  : "O usuário será apenas desvinculado da sua empresa. As outras empresas que ele atende continuarão intactas."}
              </Typography>
            </div>

            {impact.companies.map(c => {
              const p = plan[c.companyId] || {};
              const hasWork =
                c.tickets.open > 0 || c.tickets.pending > 0 || c.contactsInWallet > 0;
              return (
                <div key={c.companyId} className={classes.companyCard}>
                  <div className={classes.companyHeader}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Briefcase size={16} />
                      <span className={classes.companyName}>{c.companyName}</span>
                    </div>
                  </div>

                  <div className={classes.countsRow}>
                    <span
                      className={`${classes.countPill} ${c.tickets.open === 0 ? classes.countPillEmpty : ""}`}
                    >
                      <MessageSquare size={12} />
                      {c.tickets.open} em atendimento
                    </span>
                    <span
                      className={`${classes.countPill} ${c.tickets.pending === 0 ? classes.countPillEmpty : ""}`}
                    >
                      <MessageSquare size={12} />
                      {c.tickets.pending} aguardando
                    </span>
                    <span
                      className={`${classes.countPill} ${c.tickets.closed === 0 ? classes.countPillEmpty : ""}`}
                    >
                      <MessageSquare size={12} />
                      {c.tickets.closed} encerrados
                    </span>
                    <span
                      className={`${classes.countPill} ${c.contactsInWallet === 0 ? classes.countPillEmpty : ""}`}
                    >
                      <Users size={12} />
                      {c.contactsInWallet} contatos em carteira
                    </span>
                  </div>

                  {hasWork && (
                    <>
                      <div className={classes.modeRow}>
                        <ModeButton
                          active={p.mode === "transfer"}
                          onClick={() => updateCompanyPlan(c.companyId, { mode: "transfer" })}
                          label="Transferir"
                          classes={classes}
                        />
                        <ModeButton
                          active={p.mode === "close"}
                          onClick={() => updateCompanyPlan(c.companyId, { mode: "close" })}
                          label="Encerrar"
                          classes={classes}
                        />
                      </div>

                      {p.mode === "transfer" && (
                        <>
                          <TextField
                            select
                            label="Destino"
                            variant="outlined"
                            size="small"
                            value={p.targetUserId || ""}
                            onChange={e =>
                              updateCompanyPlan(c.companyId, {
                                targetUserId: e.target.value ? +e.target.value : null
                              })
                            }
                            className={classes.targetSelect}
                            disabled={c.availableTransferUsers.length === 0}
                          >
                            {c.availableTransferUsers.length === 0 ? (
                              <MenuItem value="" disabled>
                                Nenhum atendente disponível
                              </MenuItem>
                            ) : (
                              c.availableTransferUsers.map(u => (
                                <MenuItem key={u.id} value={u.id}>
                                  {u.name} — {u.currentOpenTickets} em atendimento
                                </MenuItem>
                              ))
                            )}
                          </TextField>

                          <div className={classes.options}>
                            <FormControlLabel
                              className={classes.optionCheck}
                              control={
                                <Checkbox
                                  checked={!!p.includeWallet}
                                  onChange={e =>
                                    updateCompanyPlan(c.companyId, {
                                      includeWallet: e.target.checked
                                    })
                                  }
                                  size="small"
                                />
                              }
                              label={`Transferir carteira de ${c.contactsInWallet} contato(s)`}
                              disabled={c.contactsInWallet === 0}
                            />
                            <FormControlLabel
                              className={classes.optionCheck}
                              control={
                                <Checkbox
                                  checked={!!p.includeClosed}
                                  onChange={e =>
                                    updateCompanyPlan(c.companyId, {
                                      includeClosed: e.target.checked
                                    })
                                  }
                                  size="small"
                                />
                              }
                              label={`Reatribuir histórico de ${c.tickets.closed} ticket(s) encerrado(s)`}
                              disabled={c.tickets.closed === 0}
                            />
                          </div>
                        </>
                      )}

                      {p.mode === "close" && (
                        <Typography className={classes.footerInfo}>
                          Os {c.tickets.open + c.tickets.pending} tickets ativos serão
                          encerrados silenciosamente (sem mensagem ao cliente). Carteira
                          de contatos será liberada (sem dono).
                        </Typography>
                      )}
                    </>
                  )}

                  {!hasWork && (
                    <Typography className={classes.footerInfo}>
                      Nenhuma ação necessária — sem tickets ativos nem carteira.
                    </Typography>
                  )}
                </div>
              );
            })}

            <Typography className={classes.footerInfo}>
              Chat interno: {impact.internalChat.channelMemberships} canal(is) e{" "}
              {impact.internalChat.messagesAuthored} mensagens enviadas.{" "}
              {impact.scope === "global"
                ? "Mensagens permanecem no histórico como anônimas."
                : "Permanecem intactas (usuário não é destruído)."}
            </Typography>
          </>
        ) : null}
      </div>

      {!submitting && (
        <div className={classes.mdActions}>
          <button
            type="button"
            onClick={onClose}
            className={classes.mdBtnGhost}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className={classes.dangerBtn}
            disabled={loading || !validation.valid}
            title={!validation.valid ? validation.reason : ""}
          >
            Excluir definitivamente
          </button>
        </div>
      )}
    </Dialog>
  );
};

export default UserDeleteImpactModal;
